import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import get from 'lodash/get';
import { PapiDeviation } from '@wix/da-papi-types';
import ElementQueries from '../../../ElementQueries';
import { MoveDeviationToPositionPayload } from '../../actions';
import { getDeviationEntityId } from '../../../redux/normalizr/schemas/deviation';
import {
  DndSortable,
  DndSortableChildrenProps,
  DndSortableElement,
} from '../../../Dnd/DndSortable';
import { DraggableType } from '../../../types/dnd';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../_hocs/withCurrentGallection';
import withCurrentGallectionSubfolders, {
  WithCurrentGallectionSubfoldersProps,
} from '../../_hocs/withCurrentGallectionSubfolders';
import withFileUpload, {
  WithFileUploadProps,
} from '../../_hocs/withFileUpload';
import { MediaSourceType } from '../../types';
import {
  getCanEditGallection,
  isSpecialFullyLockedFolder,
} from '../../helpers';
import { FileUploadInfo } from '../../../redux/uploads/types';
import UploadThumb from '../../_partials/UploadThumb';
import withCurrentMediaSource, {
  WithCurrentMediaSourceProps,
} from '../../_hocs/withCurrentMediaSource';

import s from './DeviationGrid.scss';

export interface Props {
  deviations: PapiDeviation[];
  deviationRenderer: (
    deviation: PapiDeviation,
    index: number,
    allDeviations: PapiDeviation[]
  ) => React.ReactNode;
  moveDeviationToPosition?: (payload: MoveDeviationToPositionPayload) => void;
}

const enhance = compose(
  withFileUpload,
  withCurrentMediaSource,
  withCurrentGallection,
  // TS doesn't like the fourth HOC here, makes type compilation ...complex
  // @ts-ignore
  withCurrentGallectionSubfolders
) as any;

export class DeviationGrid extends React.Component<
  Props &
    WithFileUploadProps &
    WithCurrentGallectionProps &
    WithCurrentGallectionSubfoldersProps &
    WithCurrentMediaSourceProps
> {
  shouldComponentUpdate(nextProps) {
    // We need to update the component if either the deviation or fileUploads
    // array has changed.
    if (
      nextProps.deviations.length !== this.props.deviations.length ||
      nextProps.fileUploads.length !== this.props.fileUploads.length
    ) {
      return true;
    }
    // If the deviations array has different deviationIds
    const deviationsDidUpdate = !!nextProps.deviations.find(
      (deviation, index) =>
        deviation.deviationId !==
        get(this, `props.deviations[${index}].deviationId`)
    );
    // ...or if the files that are currently uploading have different uploadIds
    // or different progress, we need to  re-render the file upload thumb
    const fileUploadsDidUpdate = !!nextProps.fileUploads.find(
      (fileUpload, index) =>
        fileUpload.uploadId !==
          get(this, `props.fileUploads[${index}].uploadId`) ||
        fileUpload.progress !==
          get(this, `props.fileUploads[${index}].progress`) ||
        fileUpload.error !== get(this, `props.fileUploads[${index}].error`)
    );
    return deviationsDidUpdate || fileUploadsDidUpdate;
  }

  render() {
    return (
      <DndSortable onDrop={this.onDndDrop} onDragOver={this.onDndDragOver}>
        {props => (
          <ElementQueries
            widthBreakpoints={[1100, 800, 700, 450]}
            className={s['grid']}
          >
            {[
              ...this.props.fileUploads
                .sort((a, b) => a.uploadId! - b.uploadId!)
                .map(this.renderFileUpload),
              ...this.props.deviations.map(this.renderDndDeviation(props)),
            ]}
          </ElementQueries>
        )}
      </DndSortable>
    );
  }

  renderDndDeviation =
    ({ draggedElementId, ...dndProps }: DndSortableChildrenProps) =>
    (deviation: PapiDeviation, index) => {
      const {
        currentMediaSource,
        currentGallection,
        deviationRenderer,
        deviations,
        moveDeviationToPosition,
      } = this.props;
      const isDragging = draggedElementId === deviation.deviationId;
      let areDeviationsSortable = false;
      if (currentMediaSource === MediaSourceType.CUSTOM_SET) {
        areDeviationsSortable = true;
      } else if (
        currentMediaSource &&
        [MediaSourceType.TIER_DEVIATIONS, MediaSourceType.TIER_POSTS].includes(
          currentMediaSource
        )
      ) {
        areDeviationsSortable = false;
      } else {
        areDeviationsSortable =
          !!moveDeviationToPosition &&
          !!currentGallection &&
          getCanEditGallection(currentGallection) &&
          !isSpecialFullyLockedFolder(currentGallection);
      }
      const entityId = getDeviationEntityId(deviation);

      return (
        <DndSortableElement
          dndType={DraggableType.MEDIA_MANAGER_DEVIATION}
          id={entityId}
          key={entityId}
          position={index}
          isSortable={areDeviationsSortable}
          {...dndProps}
        >
          {options => (
            <div
              className={classnames(
                s['grid-item'],
                isDragging && s['dragging']
              )}
              ref={options.ref}
            >
              {deviationRenderer(deviation, index, deviations)}
            </div>
          )}
        </DndSortableElement>
      );
    };

  renderFileUpload = (fileUpload: FileUploadInfo) => {
    const { currentGallection, currentSubfolderId } = this.props;
    const { file, uploadId, folderId, progress, error } = fileUpload;

    if (
      !file ||
      !currentGallection ||
      (
        currentGallection.folderId !== folderId &&
        currentSubfolderId !== folderId
      ) ||
      currentGallection.type !== MediaSourceType.STASH
    ) {
      return null;
    }

    return (
      <UploadThumb
        key={`upload-${uploadId}`}
        className={s['grid-item']}
        file={file}
        progress={progress}
        error={error}
      />
    );
  };

  findDeviationByEntityId = (
    entityId: number | string
  ): PapiDeviation | undefined =>
    this.props.deviations.find(
      deviation => getDeviationEntityId(deviation) === entityId
    );

  onDndDrop = (entityId: number | string, position: number) => {
    const deviation = this.findDeviationByEntityId(entityId);
    if (deviation) {
      this.props.moveDeviationToPosition!({
        deviation,
        position,
        moveType: 'papiOnly',
      });
    }
  };

  onDndDragOver = (entityId: number | string, position: number) => {
    const deviation = this.findDeviationByEntityId(entityId);
    if (deviation) {
      this.props.moveDeviationToPosition!({
        deviation,
        position,
        moveType: 'streamOnly',
      });
    }
  };
}

export default enhance(DeviationGrid) as React.ComponentClass<Props>;
